import React from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Shield, Plus, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { userAPI } from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';

/**
 * ManageUserDialog — edits the linked-user account for an agent: name, email,
 * and role assignments (per agency). Owns its own form state and API calls.
 * Parent supplies the linked user + agency catalog and refreshes on `onUpdated`.
 * `onRequestToggleStatus` lets the parent orchestrate opening the disable/enable
 * confirm dialog after closing this one.
 */
export const ManageUserDialog = ({
  open,
  onOpenChange,
  linkedUser,
  agencies = [],
  agent,
  onUpdated,
  onRequestToggleStatus,
}) => {
  const [info, setInfo] = React.useState({ name: '', email: '' });
  const [roles, setRoles] = React.useState([]);
  const [saving, setSaving] = React.useState(false);

  // Seed the form whenever we open with a fresh linked user.
  React.useEffect(() => {
    if (open && linkedUser) {
      setInfo({ name: linkedUser.name || '', email: linkedUser.email || '' });
      setRoles((linkedUser.roles || []).map((r) => ({ ...r })));
    }
  }, [open, linkedUser]);

  const addRole = () => {
    setRoles((prev) => [
      ...prev,
      { agency_id: agent?.agency_id || '', role: 'Agency_User', id: `new-${Date.now()}` },
    ]);
  };

  const removeRole = (index) => {
    setRoles((prev) => prev.filter((_, i) => i !== index));
  };

  const updateRole = (index, field, value) => {
    setRoles((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleSave = async () => {
    if (!linkedUser) return;
    setSaving(true);
    try {
      await userAPI.update(linkedUser.user_id, info);
      const payload = roles.map((r) => ({ agency_id: r.agency_id, role: r.role }));
      await userAPI.updateRoles(linkedUser.user_id, payload);
      toast.success('User updated successfully');
      onOpenChange(false);
      onUpdated?.();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to update user'));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = () => {
    onOpenChange(false);
    onRequestToggleStatus?.();
  };

  const isActive = linkedUser?.status === 'active';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="manage-user-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield size={18} />
            Manage User — {linkedUser?.name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-5 py-2">
          <div>
            <h4 className="text-sm font-semibold text-slate-700 mb-3">User Information</h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Name</Label>
                <Input
                  value={info.name}
                  onChange={(e) => setInfo((p) => ({ ...p, name: e.target.value }))}
                  data-testid="user-edit-name"
                />
              </div>
              <div>
                <Label className="text-xs">Email</Label>
                <Input
                  value={info.email}
                  onChange={(e) => setInfo((p) => ({ ...p, email: e.target.value }))}
                  data-testid="user-edit-email"
                />
              </div>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-slate-700">Roles & Access</h4>
              <Button size="sm" variant="outline" onClick={addRole} data-testid="add-role-btn">
                <Plus size={14} className="mr-1" /> Add Role
              </Button>
            </div>
            {roles.length === 0 && (
              <p className="text-sm text-slate-400 italic">No roles assigned. User will have no access.</p>
            )}
            <div className="space-y-2">
              {roles.map((role, index) => (
                <div
                  key={role.id || index}
                  className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg"
                  data-testid={`role-row-${index}`}
                >
                  <Select value={role.role} onValueChange={(val) => updateRole(index, 'role', val)}>
                    <SelectTrigger className="w-[180px] h-8 text-xs" data-testid={`role-select-${index}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="KR_SuperAdmin">KR Super Admin</SelectItem>
                      <SelectItem value="KR_AdminOps">KR Admin Ops</SelectItem>
                      <SelectItem value="Accounting">Accounting</SelectItem>
                      <SelectItem value="Agency_Principal">Agency Principal</SelectItem>
                      <SelectItem value="Agency_User">Agency User</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={role.agency_id || ''} onValueChange={(val) => updateRole(index, 'agency_id', val)}>
                    <SelectTrigger className="flex-1 h-8 text-xs" data-testid={`agency-select-${index}`}>
                      <SelectValue placeholder="Select Agency" />
                    </SelectTrigger>
                    <SelectContent>
                      {agencies.map((a) => (
                        <SelectItem key={a.agency_id} value={a.agency_id}>{a.agency_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                    onClick={() => removeRole(index)}
                    data-testid={`remove-role-${index}`}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
            <div>
              <Label className="text-xs font-semibold">Account Status</Label>
              <p className={`text-sm font-medium ${isActive ? 'text-emerald-600' : 'text-red-600'}`}>
                {isActive ? 'Active' : 'Disabled'}
              </p>
            </div>
            <Button
              size="sm"
              variant="outline"
              className={isActive ? 'text-red-600 border-red-200' : 'text-emerald-600 border-emerald-200'}
              onClick={handleToggleStatus}
              data-testid="toggle-status-in-dialog"
            >
              {isActive ? 'Disable Access' : 'Enable Access'}
            </Button>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-user-btn">
            {saving ? <Loader2 size={14} className="mr-1 animate-spin" /> : null}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ManageUserDialog;
